/**
 * Device history export — the file format. Pure functions; the paging loop
 * lives in api/hooks.ts (downloadHistoryExport) and the UI in
 * components/ExportHistoryDialog.tsx.
 *
 * Format: JSONL. Line 1 is a manifest (`kind: "manifest"`) describing the
 * device, the window, what was selected, per-type counts, a glossary of every
 * record type present and the known gaps. Every following line is one record
 * `{source, ts, type, id, session_id?, data}`, sections in the order
 * events → locations → releases, each ascending by time. JSONL rather than one
 * JSON document so any size stays streamable and an AI tool can sample lines.
 */
import { DEMO_MODE_LABEL, ERR_STATE_LABEL, SYS_STATE_LABEL } from '../components/ucEnums';
import { EVENT_GLOSSARY, type GlossaryEntry } from './eventGlossary';

export const EXPORT_SCHEMA_VERSION = 1;
export const EXPORT_SOURCES = ['events', 'locations', 'releases'] as const;
export type ExportSource = (typeof EXPORT_SOURCES)[number];

export interface ExportRecord {
  source: ExportSource;
  ts: string;
  type: string;
  id: string;
  session_id?: string | null;
  data: Record<string, unknown>;
}

export interface ExportCounts {
  events: Record<string, number>;
  locations: number;
  releases: number;
}

export interface ExportSelection {
  since: string;
  until: string;
  sources: ExportSource[];
  /** null = every event type, including ones not yet seen. */
  types: string[] | null;
}

/** Records the selection will produce, per the summary counts. */
export function selectedTotal(counts: ExportCounts, sel: Pick<ExportSelection, 'sources' | 'types'>): number {
  let n = 0;
  if (sel.sources.includes('events')) {
    for (const [t, c] of Object.entries(counts.events)) if (!sel.types || sel.types.includes(t)) n += c;
  }
  if (sel.sources.includes('locations')) n += counts.locations;
  if (sel.sources.includes('releases')) n += counts.releases;
  return n;
}

const toMap = (t: Record<number, string>) =>
  Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v]));

export function buildManifest(args: {
  thing: string;
  friendlyName: string | null;
  tenantId: string;
  selection: ExportSelection;
  counts: ExportCounts;
  exportedBy: string | null;
  exportedAt: string;
}) {
  const { selection: sel, counts } = args;
  const eventTypes = sel.sources.includes('events')
    ? Object.keys(counts.events).filter((t) => !sel.types || sel.types.includes(t))
    : [];
  const present = [
    ...eventTypes,
    ...(sel.sources.includes('locations') ? ['location_fix'] : []),
    ...(sel.sources.includes('releases') ? ['release_deploy'] : []),
  ];
  const glossary: Record<string, GlossaryEntry> = {};
  const undocumented: string[] = [];
  for (const t of present) {
    const g = EVENT_GLOSSARY[t];
    if (g) glossary[t] = g;
    else undocumented.push(t);
  }
  const perType: Record<string, number> = {};
  for (const t of eventTypes) perType[t] = counts.events[t] ?? 0;

  return {
    kind: 'manifest' as const,
    schema_version: EXPORT_SCHEMA_VERSION,
    description:
      'Artome device history export. One JSON object per line after this manifest. ' +
      'Each record: {source, ts, type, id, session_id?, data}. Sections are ordered ' +
      'events → locations → releases, each ascending by ts. Records sharing a ' +
      'session_id belong to the same demo session.',
    device: { thing: args.thing, friendly_name: args.friendlyName, tenant_id: args.tenantId },
    window: {
      since: sel.since,
      until: sel.until,
      note: 'Half-open [since, until). All timestamps are UTC (ISO 8601); the device clock runs in UTC.',
    },
    selection: { sources: sel.sources, event_types: sel.types ?? 'all' },
    counts: {
      total: selectedTotal(counts, sel),
      events_by_type: perType,
      ...(sel.sources.includes('locations') ? { locations: counts.locations } : {}),
      ...(sel.sources.includes('releases') ? { releases: counts.releases } : {}),
    },
    exported_at: args.exportedAt,
    exported_by: args.exportedBy,
    glossary,
    undocumented_types: undocumented,
    enums: {
      sys_state: toMap(SYS_STATE_LABEL),
      err_state: toMap(ERR_STATE_LABEL),
      demo_mode: toMap(DEMO_MODE_LABEL),
    },
    known_gaps: [
      'Releases: only deploys targeted at this device directly. Deploys to a thing group the device belongs to are not included.',
      'Not included: microcontroller debug logs, host telemetry metrics (CPU, signal, temperature), shadow-document history (not stored).',
      'Reboot and log-bundle requests are recorded only when the device executed them (remote_reboot), not when requested.',
    ],
  };
}

export type Manifest = ReturnType<typeof buildManifest>;

/** JSONL parts for a Blob: manifest first, one line per record. */
export function toJsonlParts(manifest: Manifest, records: ExportRecord[]): string[] {
  const parts = [JSON.stringify(manifest) + '\n'];
  for (const r of records) parts.push(JSON.stringify(r) + '\n');
  return parts;
}

/** `artome-<thing>-20260901T0000Z_20260928T0000Z.jsonl` — sortable, filesystem-safe. */
export function exportFilename(thing: string, since: string, until: string): string {
  const stamp = (iso: string) => iso.slice(0, 16).replace(/[-:]/g, '') + 'Z';
  const safe = thing.replace(/[^A-Za-z0-9._-]/g, '_');
  return `artome-${safe}-${stamp(since)}_${stamp(until)}.jsonl`;
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
